import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  /** Estilos del botón disparador. Por defecto, compacto (para barras de filtros). */
  buttonClassName?: string;
  /** Estilos del menú desplegable (ancho, etc.). */
  menuClassName?: string;
}

const DEFAULT_BUTTON_CLASS =
  'flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800';

/** Desplegable con selección múltiple (checkboxes). Sin selección = filtro inactivo. */
export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  disabled = false,
  buttonClassName,
  menuClassName = 'w-56',
}: MultiSelectFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={disabled}
        className={buttonClassName ?? DEFAULT_BUTTON_CLASS}
      >
        <span className="truncate">{label}</span>
        <span className="ml-auto flex shrink-0 items-center gap-1.5 pl-1.5">
          {selected.length > 0 && (
            <span className="rounded-full bg-teal-600 px-1.5 text-xs font-semibold text-white">{selected.length}</span>
          )}
          <Icon path="M6 9l6 6 6-6" className="h-4 w-4" />
        </span>
      </button>

      {open && (
        <div
          className={`absolute left-0 z-30 mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-800 dark:bg-gray-900 ${menuClassName}`}
        >
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full px-3 py-1 text-left text-xs font-semibold text-teal-600 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {t('metrics.caja.detail.clear')}
            </button>
          )}
          {options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span className="truncate">{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
